import { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Database, Shield, Brain, Sparkles, ArrowRight, X, Lock, Search, BarChart, Layers, Cloud, CheckCircle, AlertCircle } from 'lucide-react';
import LeadCaptureModal from '../../../components/LeadCaptureModal';

export default function PawareDataVersion_1_0() {
  const [openFaq, setOpenFaq] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const [modalState, setModalState] = useState({
    isOpen: false,
    source: 'sdk',
    buttonType: 'orcamento'
  });

  const openModal = (buttonType = 'orcamento') => {
    setModalState({
      isOpen: true,
      source: 'sdk',
      buttonType
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const components = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Processamento Inteligente",
      description: "Transforme documentos em dados estruturados",
      fullDescription: "Pipeline completa de processamento que transforma PDFs, imagens, DOCX e Excel em dados estruturados prontos para análise. OCR avançado, extração de tabelas, reconhecimento de layouts e conversão automática para formatos utilizáveis.",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Segurança & Compliance",
      description: "Detecção e proteção de dados sensíveis",
      fullDescription: "Sistema inteligente de detecção de informações sensíveis como CPF, RG, emails, telefones e dados bancários. Anonimização automática, logs de auditoria e compliance com LGPD/GDPR integrados ao pipeline.",
      gradient: "from-blue-500 to-indigo-500"
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Estruturação Semântica",
      description: "Preparação para bancos vetoriais",
      fullDescription: "Conversão otimizada de documentos para embeddings semânticos. Chunking inteligente, preservação de contexto, metadados enriquecidos e indexação otimizada para RAG (Retrieval Augmented Generation).",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: <BarChart className="w-8 h-8" />,
      title: "Analytics Avançado",
      description: "Insights automáticos dos seus dados",
      fullDescription: "Extração automática de métricas, estatísticas e padrões. Geração de dashboards, identificação de anomalias, análise de tendências e relatórios customizados baseados no conteúdo processado.",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Pipeline Customizável",
      description: "Fluxos sob medida para seu negócio",
      fullDescription: "Criamos pipelines personalizados para suas necessidades específicas. Integração com seus sistemas, regras de negócio customizadas, validações específicas e processamento adaptado ao seu contexto.",
      gradient: "from-cyan-500 to-blue-500"
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Processamento Escalável",
      description: "De documentos únicos a milhões",
      fullDescription: "Infraestrutura elástica que processa desde um documento até milhões em paralelo. Processamento em lote otimizado, filas inteligentes e opção de processamento em nossos servidores de alta performance.",
      gradient: "from-pink-500 to-rose-500"
    }
  ];

  const faqs = [
    {
      question: "Como funciona o processamento sob medida?",
      answer: "Nossa equipe analisa seus tipos de documentos, volumes e necessidades específicas para criar um pipeline customizado. Isso inclui regras de validação personalizadas, integrações com seus sistemas e otimizações específicas para seu caso de uso."
    },
    {
      question: "Posso processar documentos sensíveis com segurança?",
      answer: "Sim! O SDK inclui detecção automática de dados sensíveis (CPF, RG, dados bancários), criptografia em trânsito e repouso, opção de processamento on-premise, e logs completos para auditoria. Somos compatíveis com LGPD e GDPR."
    },
    {
      question: "Qual o volume de documentos suportado?",
      answer: "Desde um único documento até milhões por dia. Nossa arquitetura escala automaticamente, com processamento paralelo e otimizações para grandes volumes. Oferecemos também processamento em nossos servidores de alta performance quando necessário."
    },
    {
      question: "Como integro com meu sistema atual?",
      answer: "O SDK oferece APIs REST, webhooks, integrações nativas com principais clouds (AWS, Google, Azure), e podemos desenvolver conectores customizados. Suportamos também processamento via SFTP, filas de mensagens e streaming."
    }
  ];

  const pipelineSteps = [
    { step: 1, name: "Ingestão", icon: <FileText className="w-6 h-6" />, description: "Upload seguro de documentos" },
    { step: 2, name: "Classificação", icon: <Search className="w-6 h-6" />, description: "Identificação automática de tipos" },
    { step: 3, name: "Extração", icon: <Brain className="w-6 h-6" />, description: "OCR e análise inteligente" },
    { step: 4, name: "Validação", icon: <CheckCircle className="w-6 h-6" />, description: "Verificação e limpeza de dados" },
    { step: 5, name: "Estruturação", icon: <Database className="w-6 h-6" />, description: "Organização para uso final" },
    { step: 6, name: "Entrega", icon: <Cloud className="w-6 h-6" />, description: "APIs, webhooks ou download" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-blue-50">
      {/* Animated Background - mais suave para combinar com tema de dados */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-teal-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Component Modal */}
      {selectedComponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedComponent(null)}>
          <div className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedComponent(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className={`w-16 h-16 bg-gradient-to-r ${selectedComponent.gradient} rounded-xl flex items-center justify-center mb-6 text-white`}>
              {selectedComponent.icon}
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{selectedComponent.title}</h3>
            <p className="text-gray-600 leading-relaxed">{selectedComponent.fullDescription}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="relative bg-white/80 backdrop-blur-lg shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Distribuído por</span>
              <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Paware Softwares
              </span>
            </div>
            <button 
              onClick={() => window.location.href = '/'} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-lg transition-all duration-300 hover:bg-gray-50"
            >
              ← Voltar ao Início
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative container mx-auto px-6 pt-20 pb-16">
        <div className="text-center max-w-5xl mx-auto">
          <div className="mb-6 inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full border border-emerald-200">
            <Sparkles className="w-4 h-4 text-emerald-600 mr-2" />
            <span className="text-sm text-emerald-700 font-medium">Pipeline Inteligente de Processamento de Dados</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Paware Data
            <span className="block text-3xl md:text-4xl mt-2 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              SDK de Processamento Inteligente
            </span>
          </h1>
          <div className="text-2xl font-light text-gray-600 mb-8">
            Versão 1.0
          </div>
          
          <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-3xl mx-auto">
            Transforme documentos não estruturados em dados valiosos com nosso pipeline 
            <span className="text-emerald-600 font-semibold"> inteligente e customizável</span>. 
            De PDFs escaneados a planilhas complexas, processamos tudo com 
            <span className="text-teal-600 font-semibold"> segurança e precisão</span>.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center space-x-2 px-4 py-2 bg-emerald-100 rounded-full">
              <FileText className="w-5 h-5 text-emerald-600" />
              <span className="text-gray-700">Multi-formato</span>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-blue-100 rounded-full">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="text-gray-700">LGPD Compliant</span>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-purple-100 rounded-full">
              <Brain className="w-5 h-5 text-purple-600" />
              <span className="text-gray-700">IA Avançada</span>
            </div>
          </div>

          <button 
            onClick={() => openModal('saiba_mais')}
            className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg font-semibold text-white shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
          >
            <span className="flex items-center">
              Solicitar Demonstração
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      </section>

      {/* Pipeline Visualization */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pipeline de Processamento
            </h2>
            <p className="text-lg md:text-xl text-gray-600">Do documento bruto ao dado estruturado</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {pipelineSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 h-full">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white mx-auto mb-3">
                      {step.icon}
                    </div>
                    <div className="text-xs text-emerald-600 font-semibold mb-1">Etapa {step.step}</div>
                    <h4 className="font-semibold text-gray-900 mb-2">{step.name}</h4>
                    <p className="text-xs text-gray-600">{step.description}</p>
                  </div>
                  {index < pipelineSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-2 transform -translate-y-1/2">
                      <ArrowRight className="w-4 h-4 text-emerald-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Components Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recursos Poderosos
            </h2>
            <p className="text-lg md:text-xl text-gray-600">Clique para conhecer cada funcionalidade</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-6xl mx-auto">
            {components.map((component, index) => (
              <div
                key={index}
                onClick={() => setSelectedComponent(component)}
                className="group relative cursor-pointer"
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${component.gradient} rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                <div className="relative bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:bg-white hover:shadow-lg transition-all duration-300 hover:scale-105 hover:border-transparent w-40 h-40 flex flex-col items-center justify-center">
                  <div className={`w-16 h-16 bg-gradient-to-r ${component.gradient} rounded-xl flex items-center justify-center mb-3 text-white`}>
                    {component.icon}
                  </div>
                  <h3 className="text-xs font-semibold text-gray-900 text-center leading-tight">{component.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Security Features */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="container mx-auto px-6">
          <div className="text-center text-white">
            <AlertCircle className="w-16 h-16 mx-auto mb-6 text-blue-200" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Segurança em Primeiro Lugar
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <Lock className="w-10 h-10 mx-auto mb-3 text-blue-300" />
                <h3 className="font-semibold mb-2">Criptografia Total</h3>
                <p className="text-blue-100 text-sm">Em trânsito e repouso, seus dados sempre protegidos</p>
              </div>
              <div className="text-center">
                <Shield className="w-10 h-10 mx-auto mb-3 text-blue-300" />
                <h3 className="font-semibold mb-2">LGPD/GDPR</h3>
                <p className="text-blue-100 text-sm">Compliance total com regulamentações de privacidade</p>
              </div>
              <div className="text-center">
                <Database className="w-10 h-10 mx-auto mb-3 text-blue-300" />
                <h3 className="font-semibold mb-2">On-Premise</h3>
                <p className="text-blue-100 text-sm">Opção de processar em seus próprios servidores</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Perguntas Frequentes
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden hover:bg-gray-100 transition-colors">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400">© 2025 Paware Data SDK v1.0</p>
              <p className="text-sm text-gray-500">
                Distribuído por <span className="text-emerald-400 font-semibold">Paware Softwares</span>
              </p>
            </div>
            <div className="text-sm text-gray-400 text-center md:text-right">
              <p>Pipeline customizável para suas necessidades</p>
              <p className="text-xs text-gray-500 mt-1">Processamento seguro e em conformidade</p>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
      <LeadCaptureModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        source={modalState.source}
        buttonType={modalState.buttonType}
      />
    </div>
  );
}